import { config } from '../config';
import type { Status, StatusOverride } from '../types';

/**
 * Design §3.3 — the one expression that writes `Hypothesis.status`.
 *
 * BUILD-UNIT NOTE: U-0 took this file as a declared thin slice, because the transaction it measures
 * writes the real `{cachedCount, status, lastIncrementTs}` patch (design §5.1 step 4) and a harness
 * that hard-coded `status` would be measuring a transaction the product does not perform. **U-2 now
 * owns it.** It was already the whole of `domain/status.ts` as designed — one expression — so U-2
 * absorbed it unchanged rather than replacing it, and its existing tests still hold.
 *
 * `status` is **derived**; `statusOverride` is what is persisted and what a user sets. Two
 * dimensions, because the requirements already assume two: INV-3's "a hypothesis leaving either
 * returns to `confirmed` or `tracking` per its current count" is only well-defined if the count
 * survived the disprove — it was **masked, not destroyed**. A single-dimension enum cannot express
 * masking.
 *
 * Auto-confirm (INV-3), undo reversal, LIVE-11's clear-on-increment, and "leaving `disproved`
 * returns to `confirmed` or `tracking` per its current count" are all this same expression. There
 * is no branch to get wrong, and INV-3's "no other automatic transitions exist" holds because no
 * other code writes `status`:
 *
 *   - `domain/counts.ts` routes every count change through `deriveStatus` (`withDerivedStatus`).
 *   - `domain/order.ts` reads the **override**, never this value, so status cannot reach the render
 *     order and launder a count into it (INV-11, LIVE-12).
 *
 * @param override the dismissal dimension — user-set, reversible, `null` when not dismissed
 * @param count    `cachedCount`, the evidence dimension
 */
export function deriveStatus(override: StatusOverride, count: number): Status {
  return override ?? (count >= config.PATTERN_THRESHOLD ? 'confirmed' : 'tracking');
}
